import * as yup from "yup";
import React, { FC } from "react";
import { Block } from "baseui/block";
import { Button } from "kanopi-components/Button";
import { expandPadding } from "kanopi-components/utils/styles";
import { FixedFooter } from "kanopi-components/Sticky/FixedFooter";
import { Form } from "kanopi-components/Form";
import { FormControl, Input } from "kanopi-components/Input";
import { HeadingMedium, ParagraphSmall } from "baseui/typography";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { useForm } from "react-weaver";
import { useRegister } from "kw/auth/hooks/useRegister";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useUser } from "kw/auth/useUser";
import { WidthContainer } from "kw/util/ChatFlow";

type TRegisterForm = {
  onRegistrationComplete?: (...args: unknown[]) => void;
  submitButtonText?: string;
  hideEmail?: boolean;
  hidePassword?: boolean;
};
export const passwordValidator = {
  password1: yup
    .string()
    .required("Enter your new password")
    .min(9, "Needs to be at least 9 characters long")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{2,}$/,
      "Needs to have at least 1 letter and 1 number"
    ),
  password2: yup
    .string()
    .required("Confirm your new password")
    .oneOf([yup.ref("password1"), null], "Both passwords need to match"),
};
export const RegisterForm: FC<TRegisterForm> = ({
  onRegistrationComplete,
  submitButtonText = "Register",
  hideEmail = false,
  hidePassword = false,
}) => {
  const showEmail = !hideEmail;
  const showPassword = !hidePassword;
  const [, theme] = useStyletron();
  const validator = yup.object().shape({
    email: yup.string().required(),
    ...passwordValidator,
  });
  const { register, loading } = useRegister();
  const user = useUser();
  const { fieldProps, errors, notReady } = useForm({
    initialValues: {
      email: user?.email,
    },
    validator,
  });
  const onRegister = async () => {
    register(
      {
        email: fieldProps.email.value,
        password1: fieldProps.password1.value,
        password2: fieldProps.password2.value,
      },
      false,
      () => {
        onRegistrationComplete?.();
      }
    );
  };
  return (
    <Form>
      <WidthContainer>
        <Block
          backgroundColor={theme.colors.backgroundSecondary}
          {...expandPadding([
            theme.sizing.scale900,
            theme.sizing.scale800,
            theme.sizing.scale900,
            theme.sizing.scale800,
          ])}
          $style={{ boxSizing: "border-box" }}
        >
          <HeadingMedium margin={0} center>
            Create your account
          </HeadingMedium>
          <ParagraphSmall marginBottom={theme.sizing.scale900}>
            Choose a password to create your account so you can come back to manage your
            insurance, make a claim, and much more.
          </ParagraphSmall>
          {showEmail && (
            <FormControl label="Email" error={errors.email}>
              <Input {...fieldProps.email} inputMode="email" required />
            </FormControl>
          )}
          {showPassword && (
            <FormControl label="Password" error={errors.password1}>
              <Input type="password" {...fieldProps.password1} required />
            </FormControl>
          )}
          {showPassword && (
            <FormControl label="Confirm password" error={errors.password2}>
              <Input type="password" {...fieldProps.password2} required />
            </FormControl>
          )}
        </Block>
        <FixedFooter>
          <Sektion>
            <Block display="flex" justifyContent="center">
              <Button
                onClick={onRegister}
                kind={Button.KIND.primary}
                type="submit"
                wide
                disabled={loading || notReady}
                isLoading={loading}
              >
                {submitButtonText}
              </Button>
            </Block>
          </Sektion>
        </FixedFooter>
      </WidthContainer>
    </Form>
  );
};
